window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  var PDP_TYPES = JRS.PDP_TYPES;
  var LOGO_TYPE_DISPLAY = JRS.LOGO_TYPE_DISPLAY;

  /**
   * @param {string} type
   * @returns {boolean}
   */
  function isValidPdpType(type) {
    return type === PDP_TYPES.STANDARD || type === PDP_TYPES.BUNDLE;
  }

  /**
   * @returns {string}
   */
  function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /** @type {import('./jrs-v2-types.js').ProductConfiguration|null} */
  var state = null;

  function assertInitialized() {
    if (!state) {
      throw new Error('ConfigurationState: init() must be called before using setters');
    }
  }

  /**
   * @param {'standard'|'bundle'} type
   * @param {Object} [bundleInfo]
   * @param {string} [bundleInfo.metaobjectId]
   * @param {string} [bundleInfo.productHandle]
   */
  function init(type, bundleInfo) {
    if (!isValidPdpType(type)) {
      throw new Error('ConfigurationState: init type must be standard or bundle');
    }
    state = {
      type: type,
      instanceId: generateUUID(),
      timestamp: new Date().toISOString(),
      categories: [],
      customization: null
    };
    if (bundleInfo) {
      if (bundleInfo.metaobjectId) state.bundleMetaobjectId = bundleInfo.metaobjectId;
      if (bundleInfo.productHandle) state.bundleProductHandle = bundleInfo.productHandle;
    }
  }

  /**
   * @param {number} index
   * @param {string} name
   */
  function setCategory(index, name) {
    assertInitialized();
    if (index > state.categories.length) {
      throw new Error(
        'ConfigurationState: setCategory would create a gap (index ' + index + ', length ' + state.categories.length + ')'
      );
    }
    if (index < state.categories.length) {
      state.categories[index].name = name;
      state.categories[index].index = index;
    } else {
      state.categories.push({ name: name, index: index, products: [] });
    }
  }

  /**
   * @param {number} categoryIndex
   * @param {number} productIndex
   * @param {import('./jrs-v2-types.js').SelectedProduct} product
   */
  function setProduct(categoryIndex, productIndex, product) {
    assertInitialized();
    if (!state.categories[categoryIndex]) {
      throw new Error('ConfigurationState: setProduct category does not exist at index ' + categoryIndex);
    }
    state.categories[categoryIndex].products[productIndex] = product;
  }

  /**
   * @param {number} categoryIndex
   * @param {number} productIndex
   * @param {import('./jrs-v2-types.js').SizeQuantity[]} sizes
   */
  function setSizes(categoryIndex, productIndex, sizes) {
    assertInitialized();
    var cat = state.categories[categoryIndex];
    if (!cat) {
      throw new Error('ConfigurationState: setSizes category does not exist at index ' + categoryIndex);
    }
    var prod = cat.products[productIndex];
    if (!prod) {
      throw new Error('ConfigurationState: setSizes product does not exist at index ' + productIndex);
    }
    if (!sizes || sizes.length === 0) {
      throw new Error('ConfigurationState: setSizes sizes must be non-empty');
    }
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i].quantity <= 0) {
        throw new Error('ConfigurationState: setSizes every quantity must be > 0');
      }
    }
    prod.sizes = sizes;
  }

  /**
   * @param {import('./jrs-v2-types.js').PositionAssignment[]} positions
   */
  function setPositions(positions) {
    assertInitialized();
    if (!state.customization) {
      state.customization = { positions: [] };
    }
    state.customization.positions = positions;
  }

  /**
   * @param {string} positionId
   * @param {import('./jrs-v2-types.js').LogoConfiguration} logo
   */
  function setPositionLogo(positionId, logo) {
    assertInitialized();
    if (!state.customization) {
      throw new Error('ConfigurationState: setPositionLogo requires customization');
    }
    var list = state.customization.positions;
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === positionId) {
        list[i].logo = logo;
        return;
      }
    }
    throw new Error('ConfigurationState: setPositionLogo position not found: ' + positionId);
  }

  /**
   * @param {string} sourceLogoGroupId
   * @param {import('./jrs-v2-types.js').LogoConfiguration} logo
   */
  function setLinkedPositionLogos(sourceLogoGroupId, logo) {
    assertInitialized();
    if (!state.customization) {
      throw new Error('ConfigurationState: setLinkedPositionLogos requires customization');
    }
    var list = state.customization.positions;
    for (var i = 0; i < list.length; i++) {
      if (list[i].sourceLogoGroupId === sourceLogoGroupId) {
        list[i].logo = logo;
      }
    }
  }

  function clearCustomization() {
    assertInitialized();
    state.customization = null;
  }

  /**
   * @returns {import('./jrs-v2-types.js').ProductConfiguration}
   */
  function getConfiguration() {
    assertInitialized();
    return JSON.parse(JSON.stringify(state));
  }

  /**
   * @returns {number}
   */
  function getTotalQuantity() {
    assertInitialized();
    var total = 0;
    for (var c = 0; c < state.categories.length; c++) {
      var prods = state.categories[c].products;
      for (var p = 0; p < prods.length; p++) {
        var prod = prods[p];
        if (!prod || !prod.sizes) continue;
        for (var s = 0; s < prod.sizes.length; s++) {
          total += prod.sizes[s].quantity;
        }
      }
    }
    return total;
  }

  /**
   * @param {string} str
   * @returns {boolean}
   */
  function nonEmptyString(str) {
    return typeof str === 'string' && str.length > 0;
  }

  /**
   * @returns {{ ready: boolean, reason: string|null }}
   */
  function isReady() {
    assertInitialized();
    if (state.categories.length === 0) {
      return { ready: false, reason: 'No categories configured' };
    }
    var hasQty = false;
    for (var c = 0; c < state.categories.length; c++) {
      var prods = state.categories[c].products;
      for (var p = 0; p < prods.length; p++) {
        var prod = prods[p];
        if (!prod || !prod.sizes) continue;
        for (var s = 0; s < prod.sizes.length; s++) {
          if (prod.sizes[s].quantity > 0) {
            hasQty = true;
            break;
          }
        }
        if (hasQty) break;
      }
      if (hasQty) break;
    }
    if (!hasQty) {
      return { ready: false, reason: 'No size with quantity > 0' };
    }
    if (state.customization !== null) {
      var positions = state.customization.positions;
      if (!positions || positions.length === 0) {
        return { ready: false, reason: 'Customization requires at least one position' };
      }
      for (var i = 0; i < positions.length; i++) {
        var pos = positions[i];
        var logo = pos.logo;
        if (!nonEmptyString(logo.logoId)) {
          return { ready: false, reason: 'Position missing logoId' };
        }
        if (logo.type === 'upload' || logo.type === 'existing') {
          if (!nonEmptyString(logo.url) || logo.url.indexOf('https://') !== 0) {
            return { ready: false, reason: 'Upload/existing logo requires https URL' };
          }
        }
        if (logo.type === 'text') {
          if (!logo.text) {
            return { ready: false, reason: 'Text logo missing text data' };
          }
          var lines = logo.text.lines;
          var anyLine = false;
          for (var L = 0; L < 4; L++) {
            if (lines[L] && String(lines[L]).trim() !== '') {
              anyLine = true;
              break;
            }
          }
          if (!anyLine) {
            return { ready: false, reason: 'Text logo needs at least one non-empty line' };
          }
        }
      }
    }
    return { ready: true, reason: null };
  }

  /**
   * @param {Object} pos
   * @returns {import('./jrs-v2-types.js').LogoConfiguration}
   */
  function hydrateLogoFromBundlePosition(pos) {
    var logoType = pos.logoType || pos['Logo Type'] || '';
    var logoId = pos['Logo Id'] || (pos.logo && pos.logo.logoId) || '';
    var logoUrl = (pos.logo && pos.logo.url) || pos['Logo'] || null;

    if (logoType === LOGO_TYPE_DISPLAY.TEXT || logoType === 'text') {
      return {
        type: 'text',
        logoId: String(logoId),
        url: null,
        filename: null,
        text: {
          lines: [
            pos['Logo Text'] || '',
            pos['Logo Text 2'] || '',
            pos['Logo Text 3'] || '',
            pos['Logo Text 4'] || ''
          ],
          colour: pos['Logo Color'] || (pos.logo && pos.logo.color) || '',
          font: pos['Logo Font'] || (pos.logo && pos.logo.font) || ''
        },
        note: pos.Note || ''
      };
    }

    if (logoType === LOGO_TYPE_DISPLAY.EXISTING || logoType === 'existing') {
      return {
        type: 'existing',
        logoId: String(logoId),
        url: logoUrl,
        filename: null,
        text: null,
        note: pos.Note || ''
      };
    }

    return {
      type: 'upload',
      logoId: String(logoId),
      url: logoUrl,
      filename: null,
      text: null,
      note: pos.Note || ''
    };
  }

  /**
   * @param {Object<string, any>} props
   * @param {number} n
   * @param {string} logoTypeRaw
   * @param {string|null} logoId
   * @returns {import('./jrs-v2-types.js').LogoConfiguration}
   */
  function hydrateLogoFromNumberedProperties(props, n, logoTypeRaw, logoId) {
    if (logoTypeRaw === LOGO_TYPE_DISPLAY.TEXT) {
      return {
        type: 'text',
        logoId: String(logoId),
        url: null,
        filename: null,
        text: {
          lines: [
            props['Logo Text ' + n] || '',
            props['Logo Text 2 ' + n] || '',
            props['Logo Text 3 ' + n] || '',
            props['Logo Text 4 ' + n] || ''
          ],
          colour: props['Logo Color ' + n] || '',
          font: props['Logo Font ' + n] || ''
        },
        note: props['Note' + n] || props['Note ' + n] || props['_Note ' + n] || ''
      };
    }

    if (logoTypeRaw === LOGO_TYPE_DISPLAY.EXISTING) {
      return {
        type: 'existing',
        logoId: String(logoId),
        url: props['Logo' + n] || props['_Logo ' + n] || props['Logo ' + n] || null,
        filename: null,
        text: null,
        note: props['Note' + n] || props['Note ' + n] || props['_Note ' + n] || ''
      };
    }

    return {
      type: 'upload',
      logoId: String(logoId),
      url: props['Logo' + n] || props['_Logo ' + n] || props['Logo ' + n] || null,
      filename: null,
      text: null,
      note: props['Note' + n] || props['Note ' + n] || props['_Note ' + n] || ''
    };
  }

  /**
   * @param {Array<{ categoryIndex: number }>} bundleRaw
   * @returns {Array}
   */
  function sortBundleRawByCategoryIndex(bundleRaw) {
    var copy = bundleRaw.slice();
    copy.sort(function(a, b) {
      var ai = a && a.categoryIndex != null ? Number(a.categoryIndex) : 0;
      var bi = b && b.categoryIndex != null ? Number(b.categoryIndex) : 0;
      return ai - bi;
    });
    return copy;
  }

  /**
   * @param {Object<string, any>} parentProperties
   * @param {import('./jrs-v2-types.js').ShopifyCartItem[]} [childItems]
   */
  function hydrate(parentProperties, childItems) {
    void childItems;
    var isBundleItem = Object.prototype.hasOwnProperty.call(parentProperties, '_bundle_raw');
    var type = isBundleItem ? PDP_TYPES.BUNDLE : PDP_TYPES.STANDARD;
    var categories = [];
    var positions = [];

    if (isBundleItem) {
      var _braw = parentProperties['_bundle_meta'] || parentProperties['_bundle_raw']; // meta-first (2026-08-20)
      var bundleRaw = (typeof _braw === 'object' && _braw !== null) ? _braw : JSON.parse(_braw);
      var sorted = sortBundleRawByCategoryIndex(bundleRaw);
      for (var ci = 0; ci < sorted.length; ci++) {
        var cat = sorted[ci];
        var products = [];
        var productKeys = Object.keys(cat.products || {});
        for (var k = 0; k < productKeys.length; k++) {
          var p = cat.products[productKeys[k]];
          var sizes = [];
          var sizeKeys = Object.keys(p.sizes || {});
          for (var s = 0; s < sizeKeys.length; s++) {
            var sk = sizeKeys[s];
            sizes.push({
              size: sk,
              quantity: p.sizes[sk],
              variantId: p.variant_id,
              sku: null
            });
          }
          products.push({
            productId: p.product_id,
            productHandle: p.handle,
            productTitle: p.name,
            variantId: p.variant_id,
            colour: p.color,
            imageUrl: p.image || null,
            sizes: sizes
          });
        }
        categories.push({ name: cat.name, index: cat.categoryIndex, products: products });
      }
      for (var bci = 0; bci < sorted.length; bci++) {
        var catPos = sorted[bci];
        var catPositions = catPos.positions || [];
        for (var pi = 0; pi < catPositions.length; pi++) {
          var pos = catPositions[pi];
          var appMethod = pos.applicationMethod || pos['Application Method'];
          positions.push({
            id: generateUUID(),
            positionName: pos.name,
            positionVariantId: pos.logoVariantId,
            applicationMethod: appMethod,
            categoryIndex: catPos.categoryIndex,
            sourceLogoGroupId: null,
            logo: hydrateLogoFromBundlePosition(pos)
          });
        }
      }
    } else {
      var customData = JSON.parse(parentProperties['_customization_data']);
      var parentVariantId = Number(customData.parentVariantId);
      categories = [{
        name: parentProperties['_product_title'] || 'Product',
        index: 0,
        products: [{
          productId: 0,
          productHandle: '',
          productTitle: parentProperties['_product_title'] || '',
          variantId: parentVariantId,
          colour: '',
          imageUrl: null,
          sizes: [{ size: '', quantity: 1, variantId: parentVariantId, sku: null }]
        }]
      }];
      var cdPositions = customData.positions || [];
      for (var i = 0; i < cdPositions.length; i++) {
        var cdPos = cdPositions[i];
        var n = i + 1;
        var logoTypeRaw = parentProperties['Logo Type' + n] || parentProperties['_Logo Type ' + n] || parentProperties['Logo Type ' + n] || '';
        positions.push({
          id: generateUUID(),
          positionName: cdPos.position,
          positionVariantId: Number(cdPos.variantId),
          applicationMethod: cdPos.applicationMethod,
          categoryIndex: 0,
          sourceLogoGroupId: null,
          logo: hydrateLogoFromNumberedProperties(parentProperties, n, logoTypeRaw, cdPos.logoId)
        });
      }
    }

    state = {
      type: type,
      instanceId: generateUUID(),
      timestamp: new Date().toISOString(),
      categories: categories,
      customization: positions.length > 0 ? { positions: positions } : null
    };

    if (isBundleItem) {
      if (parentProperties['_bundle_metaobject_id']) {
        state.bundleMetaobjectId = String(parentProperties['_bundle_metaobject_id']);
      }
      if (parentProperties['_bundle_product_handle']) {
        state.bundleProductHandle = String(parentProperties['_bundle_product_handle']);
      }
    }
  }

  function reset() {
    state = null;
  }

  JRS.generateUUID = generateUUID;

  JRS.ConfigurationState = {
    init: init,
    setCategory: setCategory,
    setProduct: setProduct,
    setSizes: setSizes,
    setPositions: setPositions,
    setPositionLogo: setPositionLogo,
    setLinkedPositionLogos: setLinkedPositionLogos,
    clearCustomization: clearCustomization,
    getConfiguration: getConfiguration,
    getTotalQuantity: getTotalQuantity,
    isReady: isReady,
    hydrate: hydrate,
    reset: reset
  };
})(window.JRS);
